import { type ReactElement, type Ref } from 'react';
import { Pressable, Text, View } from 'react-native';
import type {
  SessionResult,
  WorkoutEnergyReadout,
} from '@heraion/shared';
import { CaloriesEffortBlock, Icon } from '../index';

import { WorkoutTotalsCard } from './Totals';
import { workoutTotals } from './totalsMath';
import { WorkoutHeatMap } from './WorkoutHeatMap';
import type { LoggerDraft, WeightUnit } from './state/loggerDraftTypes';
import type { makeStyles } from './loggerStyles';
import type { useTheme } from './theme';
import type { LoggerHostDeps } from './hostDeps';
import type {
  PostSaveSummaryState,
  PostSessionSummaryHandle,
} from './usePostSaveSummary';
import { slugAsWords } from './exerciseName';

// Below-the-entry-list footer + post-save region. Extracted verbatim out
// of LoggerScreen (Track R). Renders, in order: the
// workout totals card and body map (only while logging,
// i.e. postSaveSummary === null) followed by the post-session summary
// card and the post-save Share/Done actions. The save errors live in the
// header, beside Save (#45). Presentational: every action routes back
// through dispatch / the passed handlers.
type WorkoutMetaFooterProps = {
  postSaveSummary: PostSaveSummaryState | null;
  sessionResultValue: SessionResult | null;
  /** Energy readout (calories + effort/HR): the live estimate while logging, or
   *  the STORED estimate when editing an enriched session (B2c). null when
   *  nothing to show. HC-8. */
  energyReadout?: WorkoutEnergyReadout | null;
  totals: ReturnType<typeof workoutTotals>;
  draft: LoggerDraft;
  postSaveSharing: boolean;
  sharePostSave: () => void;
  defaultUnit: WeightUnit;
  locale: string;
  summaryCardRef: Ref<PostSessionSummaryHandle>;
  styles: ReturnType<typeof makeStyles>;
  theme: ReturnType<typeof useTheme>;
  t: (k: string, opts?: Record<string, unknown>) => string;
  PostSessionSummaryCard: LoggerHostDeps['PostSessionSummaryCard'];
  profile: import('./hostDeps').LoggerProfile;
  /** Slug to the exercise's name, for the MAX line (#182). */
  resolveName?: (slug: string, fallback?: string) => string;
};

export function WorkoutMetaFooter({
  postSaveSummary,
  sessionResultValue,
  energyReadout,
  totals,
  draft,
  postSaveSharing,
  sharePostSave,
  defaultUnit,
  locale,
  summaryCardRef,
  styles,
  theme,
  t,
  PostSessionSummaryCard,
  profile,
  resolveName,
}: WorkoutMetaFooterProps): ReactElement {
  // #182 (Antenore 06/10): the save's result says, in words, which MAX it
  // proposed. The proposal itself waits in the notifications.
  const maxProposed = postSaveSummary?.maxProposed ?? [];
  const failedNotes = postSaveSummary?.failedNotes ?? [];
  const maxLine =
    maxProposed.length > 0
      ? t('mobile.postSession.maxProposed', {
          count: maxProposed.length,
          exercises: maxProposed
            .map((slug) => resolveName?.(slug, slugAsWords(slug)) ?? slugAsWords(slug))
            .join(', '),
        })
      : null;
  return (
    <>
      {postSaveSummary === null ? (
        <>
          {/* The add-exercise button that used to open this block could not
              render on either mount: the timeline logger passed
              hideAddExercise, and LoggerScreen only mounts this footer in its
              postSaveSummary !== null branch, where the gate above is already
              false. Removed with PE-22 fetta E, which gave the live copy a
              home in the roster strip. */}
          {totals.sets > 0 ? (
            <WorkoutTotalsCard
              totals={totals}
              locale={locale}
              unit={defaultUnit}
              theme={theme}
              t={t}
            />
          ) : null}
          {totals.sets > 0 && energyReadout ? (
            // boxed: this block's siblings here (WorkoutTotalsCard,
            // WorkoutHeatMap below) are cards — DS-18 gives it the same
            // chrome instead of floating with only its internal divider.
            <CaloriesEffortBlock theme={theme} {...energyReadout} boxed />
          ) : null}
          {totals.sets > 0 ? (
            <WorkoutHeatMap byMuscle={totals.byMuscle} profile={profile} />
          ) : null}
        </>
      ) : null}
      {/* The summary card shows ONLY after a fresh save. A past session shows
          its summary, with its share, before the editor opens (#318); the
          editor keeps the live logger UI above. The card's own Share button
          hides: the post-save row below has one. */}
      {postSaveSummary !== null && sessionResultValue !== null ? (
        <PostSessionSummaryCard
          ref={summaryCardRef}
          result={sessionResultValue}
          energy={energyReadout ?? null}
          locale={locale}
          unit={defaultUnit}
          hideShareButton
          notes={draft.noteEvents ?? []}
        />
      ) : null}
      {postSaveSummary !== null && maxLine !== null ? (
        <Text style={styles.postSaveMaxLine}>{maxLine}</Text>
      ) : null}
      {failedNotes.length > 0 ? (
        <View style={styles.postSaveNoteFailed} accessibilityRole="alert">
          <Icon name="warn" size={theme.typography.sizes.base} color={theme.palette.danger} />
          <View style={styles.postSaveNoteFailedBody}>
            <Text style={styles.postSaveNoteFailedText}>
              {t('mobile.postSession.noteNotSaved', { count: failedNotes.length })}
            </Text>
            {failedNotes.map((text, i) => (
              <Text key={i} selectable style={styles.postSaveNoteFailedText}>
                {text}
              </Text>
            ))}
          </View>
        </View>
      ) : null}
      {postSaveSummary !== null && sessionResultValue !== null ? (
        <View style={styles.postSaveActions}>
          <Pressable
            accessibilityRole="button"
            style={({ pressed }) => [
              styles.postSaveShareBtn,
              pressed && styles.postSaveBtnPressed,
              postSaveSharing && styles.postSaveBtnDisabled,
            ]}
            onPress={() => void sharePostSave()}
            disabled={postSaveSharing}
          >
            <Text style={styles.postSaveShareText}>
              {postSaveSharing
                ? t('mobile.postSession.sharing', {
                    defaultValue: 'Preparing…',
                  })
                : t('mobile.postSession.shareCta', {
                    defaultValue: 'Share session card',
                  })}
            </Text>
          </Pressable>
          <Pressable
            accessibilityRole="button"
            style={({ pressed }) => [
              styles.postSaveDoneBtn,
              pressed && styles.postSaveBtnPressed,
            ]}
            onPress={() => postSaveSummary.onDone()}
          >
            <Text style={styles.postSaveDoneText}>
              {t('mobile.postSession.done', { defaultValue: 'Done' })}
            </Text>
          </Pressable>
        </View>
      ) : null}
    </>
  );
}
